import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Share2, 
  Download, 
  Copy, 
  Mail, 
  Instagram, 
  MessageSquare,
  QrCode,
  Check,
  X,
  Sparkles
} from 'lucide-react';
import QRCode from 'qrcode';

const SoldiersSocialCard = ({ referralCode, userName, isOpen, onClose }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  
  const baseUrl = window.location.origin;
  const referralLink = referralCode 
    ? `${baseUrl}/soldiers?ref=${referralCode}` 
    : `${baseUrl}/soldiers`;
  
  // Generate QR Code
  useEffect(() => {
    if (referralLink) {
      QRCode.toDataURL(referralLink, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      }).then(url => {
        setQrCodeUrl(url);
      }).catch(err => {
        console.error('QR Code generation error:', err);
      });
    }
  }, [referralLink]);
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join MyHairChair Soldiers - Earn While You Grow!');
    const body = encodeURIComponent(`Hey!

I wanted to share an amazing opportunity with you. MyHairChair is looking for founding partners for their HairChair Soldiers program!

🔥 What you get:
• $10-$15 bonus when your referred stylist makes their first payment
• $20-$30 total after their 2nd month
• 8% lifetime commission on all their payments FOREVER

Only 50 spots available for founding partners!

Join using my link: ${referralLink}

Let's build something great together! 💪
`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };
  
  const shareViaSMS = () => {
    const message = encodeURIComponent(`🔥 Join MyHairChair Soldiers! Earn $10-$30 bonuses + 8% lifetime commission. Only 50 founding spots! ${referralLink}`);
    window.open(`sms:?body=${message}`);
  };
  
  const shareViaInstagram = () => {
    // Copy the soldiers page link for Instagram bio/story
    const instagramMessage = `🔥 Join HairChair Soldiers! 

💰 Earn $10-$30 sign-up bonuses
📈 8% lifetime commission FOREVER
👥 Only 50 founding partner spots!

Black, White, Latino, Asian - ALL backgrounds welcome!

Apply now: ${referralLink}

#HairChairSoldiers #BarberLife #Hairstylist #SalonOwner #BeautyBusiness`;
    
    navigator.clipboard.writeText(instagramMessage);
    toast.success('Caption & link copied! Paste in your Instagram post or story.');
    window.open('https://instagram.com', '_blank');
  };

  // Diverse team image URL
  const diverseTeamImageUrl = "https://static.prod-images.emergentagent.com/jobs/f35d2631-7989-4ade-a61d-13dc62e450ea/images/94ac6f334c423deb635538373cb4c16bec168b0672e6ab8e838eb0065e6ae3f0.png";
  
  const downloadSocialCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('Canvas not available');
      return;
    }
    
    toast.loading('Generating your marketing card...');
    
    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1350; // Taller for Instagram feed (4:5 ratio)
    
    canvas.width = width;
    canvas.height = height;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#18181b');
    gradient.addColorStop(0.5, '#1c1917');
    gradient.addColorStop(1, '#18181b');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Add subtle pattern
    ctx.fillStyle = 'rgba(245, 158, 11, 0.03)';
    for (let i = 0; i < width; i += 60) {
      for (let j = 0; j < height; j += 60) {
        ctx.fillRect(i, j, 1, 60);
        ctx.fillRect(i, j, 60, 1);
      }
    }
    
    // Draw card content (without external image to avoid CORS issues)
    const drawCardContent = () => {
      // Top decorative bar
      ctx.fillStyle = 'rgba(245, 158, 11, 0.3)';
      ctx.fillRect(0, 0, width, 8);
      
      // Top badge
      ctx.fillStyle = 'rgba(245, 158, 11, 0.2)';
      roundRect(ctx, 290, 80, 500, 50, 25);
      ctx.fill();
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)';
      ctx.lineWidth = 2;
      roundRect(ctx, 290, 80, 500, 50, 25);
      ctx.stroke();
      
      ctx.fillStyle = '#f59e0b';
      ctx.font = 'bold 20px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('⭐ FOUNDING PARTNER PROGRAM ⭐', width / 2, 112);
      
      // Main title
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 80px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('HairChair', width / 2, 220);
      
      // "Soldiers" with gradient effect (simulated)
      ctx.fillStyle = '#f59e0b';
      ctx.font = 'bold 80px Arial, sans-serif';
      ctx.fillText('Soldiers', width / 2, 310);
      
      // Tagline
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '28px Arial, sans-serif';
      ctx.fillText('Join the elite 50 salon owners building', width / 2, 380);
      ctx.fillText('the future of the beauty industry.', width / 2, 415);
      
      ctx.fillStyle = '#f59e0b';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillText('Earn while you grow.', width / 2, 470);
      
      // Diversity message
      ctx.fillStyle = '#71717a';
      ctx.font = '22px Arial, sans-serif';
      ctx.fillText('Black • White • Latino • Asian - ALL Welcome!', width / 2, 520);
      
      // Stats boxes
      const stats = [
        { value: '$10-$15', label: 'Month 1 Bonus' },
        { value: '$20-$30', label: 'Month 2 Total' },
        { value: '8%', label: 'Lifetime Commission' }
      ];
      
      const boxWidth = 300;
      const boxHeight = 120;
      const startX = (width - (boxWidth * 3 + 40)) / 2;
      const boxY = 570;
      
      stats.forEach((stat, i) => {
        const x = startX + i * (boxWidth + 20);
        
        // Box background
        ctx.fillStyle = 'rgba(39, 39, 42, 0.9)';
        roundRect(ctx, x, boxY, boxWidth, boxHeight, 16);
        ctx.fill();
        
        // Box border
        ctx.strokeStyle = 'rgba(245, 158, 11, 0.3)';
        ctx.lineWidth = 2;
        roundRect(ctx, x, boxY, boxWidth, boxHeight, 16);
        ctx.stroke();
        
        // Value
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 42px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(stat.value, x + boxWidth / 2, boxY + 55);
        
        // Label
        ctx.fillStyle = '#f59e0b';
        ctx.font = '18px Arial, sans-serif';
        ctx.fillText(stat.label, x + boxWidth / 2, boxY + 90);
      });
      
      // Features section
      const features = [
        '✓ Personal referral tracking',
        '✓ Monthly payouts via Stripe',
        '✓ Exclusive founding partner perks',
        '✓ Marketing materials included'
      ];
      
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '24px Arial, sans-serif';
      ctx.textAlign = 'center';
      
      features.forEach((feature, i) => {
        ctx.fillText(feature, width / 2, 750 + (i * 40));
      });
      
      // QR Code section
      if (qrCodeUrl) {
        const qrImage = new Image();
        qrImage.onload = () => {
          // QR background
          ctx.fillStyle = '#ffffff';
          roundRect(ctx, (width - 200) / 2, 920, 200, 200, 16);
          ctx.fill();
          
          // Draw QR code
          ctx.drawImage(qrImage, (width - 180) / 2, 930, 180, 180);
          
          // Scan text
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 24px Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('SCAN TO APPLY', width / 2, 1160);
          
          // Referral code if available
          if (referralCode) {
            ctx.fillStyle = '#f59e0b';
            ctx.font = 'bold 28px Arial, sans-serif';
            ctx.fillText(`Code: ${referralCode}`, width / 2, 1200);
          }
          
          // Footer
          ctx.fillStyle = '#52525b';
          ctx.font = '18px Arial, sans-serif';
          ctx.fillText('MyHairChair.com', width / 2, 1270);
          ctx.font = '14px Arial, sans-serif';
          ctx.fillText('A GoNowBaby Media Group Brand', width / 2, 1295);
          
          if (userName) {
            ctx.fillStyle = '#f59e0b';
            ctx.font = '16px Arial, sans-serif';
            ctx.fillText(`Shared by ${userName}`, width / 2, 1325);
          }
          
          // Download
          toast.dismiss();
          const link = document.createElement('a');
          link.download = `hairchair-soldiers-invite${referralCode ? `-${referralCode}` : ''}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
          toast.success('Marketing card downloaded! Share on Instagram, Facebook, or anywhere!');
        };
        
        qrImage.onerror = () => {
          // Download without QR if it fails
          toast.dismiss();
          const link = document.createElement('a');
          link.download = `hairchair-soldiers-invite${referralCode ? `-${referralCode}` : ''}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
          toast.success('Marketing card downloaded!');
        };
        
        qrImage.src = qrCodeUrl;
      } else {
        // No QR code available, download directly
        toast.dismiss();
        const link = document.createElement('a');
        link.download = `hairchair-soldiers-invite.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Marketing card downloaded!');
      }
    };
    
    // Draw the card content directly
    drawCardContent();
  };
  
  // Helper function to draw rounded rectangles
  function roundRect(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
  
  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Share2 className="w-5 h-5 text-amber-500" />
              Share HairChair Soldiers
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Share this opportunity with your network and earn commissions!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* QR Code Preview */}
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl mb-3">
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="QR Code" className="w-40 h-40" />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center">
                    <QrCode className="w-20 h-20 text-zinc-300 animate-pulse" />
                  </div>
                )}
              </div>
              <p className="text-sm text-zinc-400">Scan to join the program</p>
              {referralCode && (
                <p className="text-amber-400 font-medium mt-1">Your Code: {referralCode}</p>
              )}
            </div>
            
            {/* Copy Link */}
            <div className="flex gap-2">
              <Input 
                value={referralLink}
                readOnly
                className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm"
              />
              <Button 
                onClick={copyToClipboard}
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800 shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={shareViaEmail}
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800 gap-2"
              >
                <Mail className="w-4 h-4 text-blue-400" />
                Email
              </Button>
              
              <Button 
                onClick={shareViaSMS}
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800 gap-2"
              >
                <MessageSquare className="w-4 h-4 text-green-400" />
                Text/SMS
              </Button>
              
              <Button 
                onClick={shareViaInstagram}
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800 gap-2"
              >
                <Instagram className="w-4 h-4 text-pink-400" />
                Instagram
              </Button>
              
              <Button 
                onClick={downloadSocialCard}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black gap-2"
              >
                <Download className="w-4 h-4" />
                Download Card
              </Button>
            </div>
            
            {/* Marketing Card Preview */}
            <Card className="bg-zinc-950 border-zinc-800 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-6 text-center">
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-medium mb-3">
                    <Sparkles className="w-3 h-3" />
                    FOUNDING PARTNER PROGRAM
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">HairChair <span className="text-amber-400">Soldiers</span></h3>
                  <p className="text-zinc-400 text-sm mb-4">Join the elite 50 salon owners</p>
                  
                  <div className="flex justify-center gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">$10-$30</div>
                      <div className="text-xs text-amber-400">Bonus</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">8%</div>
                      <div className="text-xs text-amber-400">Lifetime</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-2 w-24 h-24 mx-auto">
                    {qrCodeUrl && <img src={qrCodeUrl} alt="QR" className="w-full h-full" />}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">Scan to Join</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SoldiersSocialCard;
